import type { CopyStrategyToGroupsVariables, RunStrategyNowResponse } from '@/hooks/useStrategiesData';
import type {
  CopyStrategyToGroupsApiResponse,
  CreateStrategyResponse,
  StrategyCopyTargetApi,
  UpdateStrategyResponse,
} from '@/types/api/strategies';

/**
 * Demo-mode fabricated response for creating a strategy.
 * Used as the demoFn for useCreateStrategy.
 */
export async function createStrategyDemoResponse(): Promise<CreateStrategyResponse> {
  return {
    id: Date.now(),
    name: 'New Strategy',
    message: 'Strategy created successfully',
  };
}

/**
 * Factory for the demo-mode update-strategy response, capturing the strategyId
 * closure. Returns the demoFn for useUpdateStrategy.
 */
export function makeUpdateStrategyDemoFn(strategyId: number | string) {
  return async (): Promise<UpdateStrategyResponse> => {
    return {
      id: Number(strategyId),
      name: 'Updated Strategy',
      message: 'Strategy updated successfully',
    };
  };
}

/**
 * Demo-mode fabricated response for deleting a strategy.
 * Used as the demoFn for useDeleteStrategy.
 */
export async function deleteStrategyDemoResponse(): Promise<{ message: string }> {
  return { message: 'Strategy deleted successfully' };
}

/**
 * Demo-mode fabricated response for running a strategy now.
 * Used as the demoFn for useRunStrategyNow.
 */
export async function runStrategyNowDemoResponse(strategyId: number | string): Promise<RunStrategyNowResponse> {
  return {
    runId: Date.now(),
    strategyId: Number(strategyId),
    message: 'Strategy run triggered successfully',
  };
}

/**
 * Factory for the demo-mode update-strategy-status response, capturing the
 * strategyId closure. Returns the demoFn for useUpdateStrategyStatus.
 */
export function makeUpdateStrategyStatusDemoFn(strategyId: number | string) {
  return async (): Promise<UpdateStrategyResponse> => {
    return {
      id: Number(strategyId),
      name: 'Strategy',
      message: 'Strategy status updated successfully',
    };
  };
}

/**
 * Demo-mode fabricated response for toggling a strategy's status.
 * Used as the demoFn for useToggleStrategyStatus.
 */
export async function toggleStrategyStatusDemoResponse(data: {
  id: number | string;
  status: 'Active' | 'Inactive';
}): Promise<UpdateStrategyResponse> {
  return {
    id: Number(data.id),
    name: 'Strategy',
    message: 'Strategy status updated successfully',
  };
}

/**
 * Demo-mode company groups a strategy can be copied into.
 * Used as the demo data for useStrategyCopyTargets.
 */
export const strategyCopyTargetsDemoData: StrategyCopyTargetApi[] = [
  { companyGroupId: 201, name: 'Northern Division', companyCount: 3 },
  { companyGroupId: 202, name: 'Retail Group', companyCount: 1 },
  { companyGroupId: 203, name: 'Wholesale Group', companyCount: 2 },
];

/**
 * Demo-mode fabricated response for copying a strategy into other company groups.
 * Used as the demoFn for useCopyStrategyToGroups.
 */
export async function copyStrategyToGroupsDemoResponse(
  variables: CopyStrategyToGroupsVariables,
): Promise<CopyStrategyToGroupsApiResponse> {
  // One target collides and one is missing a template so demo mode shows both
  // partial-outcome paths the API can return.
  const collidingCompanyGroupId = 202;
  const missingTemplateCompanyGroupId = 203;

  return {
    results: variables.companyGroupIds.map((companyGroupId) =>
      companyGroupId === collidingCompanyGroupId
        ? {
            companyGroupId,
            strategyId: null,
            copied: false,
            skipReason: 'A strategy with that name already exists in this group',
            unresolvedStageTemplates: [],
          }
        : {
            companyGroupId,
            strategyId: Date.now() + companyGroupId,
            copied: true,
            skipReason: null,
            unresolvedStageTemplates:
              companyGroupId === missingTemplateCompanyGroupId
                ? [{ sortOrder: 1, templateName: 'First Reminder' }]
                : [],
          },
    ),
  };
}
